import React from 'react';
import styled, { css } from 'styled-components';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';

const Section = styled.section`
  padding: 140px 0;
  background: #FFFDF7;
  @media (min-width: 768px) { padding: 180px 0; }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  transition: opacity 1s ease, transform 1s ease;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(40px);`}
`;

const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 72px;
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
`;

const Eyebrow = styled.p`
  font-family: 'Raleway', serif;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  color: #C07020;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.8rem, 6vw, 4.5rem);
  font-weight: 300;
  font-style: italic;
  color: #1A1208;
  letter-spacing: -0.01em;
  line-height: 1.0;
`;

const Subtitle = styled.p`
  font-size: 0.88rem;
  font-weight: 300;
  color: rgba(26,18,8,0.55);
  max-width: 260px;
  text-align: right;
  line-height: 1.6;
`;

/* ─── Logo grid ──────────────────────────────────────────── */
const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  border-top: 1px solid rgba(26,18,8,0.10);
  border-bottom: 1px solid rgba(26,18,8,0.10);
  @media (min-width: 768px) { grid-template-columns: repeat(4, 1fr); }
`;

const StoreCard = styled.a`
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 56px 40px;
  position: relative;
  border-right: 1px solid rgba(26,18,8,0.10);
  border-bottom: 1px solid rgba(26,18,8,0.10);
  transition: background 0.3s;
  &:nth-child(2n) { border-right: none; }
  @media (min-width: 768px) {
    border-bottom: none;
    &:nth-child(2n) { border-right: 1px solid rgba(26,18,8,0.10); }
    &:last-child { border-right: none; }
  }
  &:hover { background: rgba(250,243,224,0.55); }
`;

const LogoBox = styled.div`
  width: 100%;
  max-width: 200px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: grayscale(100%) contrast(0.6);
    opacity: 0.55;
    transition: filter 0.35s, opacity 0.35s, transform 0.35s;
  }
  ${StoreCard}:hover & img {
    filter: grayscale(0%) contrast(1);
    opacity: 1;
    transform: scale(1.05);
  }
`;

const StoreMeta = styled.div`
  text-align: center;
`;

const StoreName = styled.p`
  font-family: 'Raleway', serif;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  color: rgba(26,18,8,0.5);
  margin-bottom: 4px;
  transition: color 0.2s;
  ${StoreCard}:hover & { color: #1A1208; }
`;

const StoreRegion = styled.p`
  font-size: 0.68rem;
  color: rgba(26,18,8,0.32);
  letter-spacing: 0.06em;
`;

const ArrowBtn = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px; height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(26,18,8,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(26,18,8,0.3);
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.2s, transform 0.2s, background 0.2s, color 0.2s;
  ${StoreCard}:hover & {
    opacity: 1;
    transform: scale(1);
    background: #7C3A00;
    border-color: #7C3A00;
    color: #FAF3E0;
  }
`;

const stores = [
  {
    name: 'TikTok Shop',
    logo: '/images/shops/1714299153tiktok-shop-logo-transparent.png',
    logoScale: 1.4,
    tag: { en: 'Mexico & USA', es: 'México y EE. UU.' },
    url: 'https://shop.tiktok.com/mx/pdp/cajeta-natural-hacienda-real-700-gr-dulce-tradicional-sin-conservadores-elaborada-con-de-vaca-sabor-/1734493917997466817',
  },
  {
    name: 'Walmart México',
    logo: '/images/shops/Walmart_logo_(2008).svg.png',
    tag: { en: 'Mexico', es: 'México' },
    url: 'https://www.walmart.com.mx/ip/cajeta-natural-hacienda-real-700-gr/00017645678905',
  },
  {
    name: 'Bodega Aurrerá',
    logo: '/images/shops/aurrera-2.svg',
    logoScale: 2,
    tag: { en: 'Mexico', es: 'México' },
    url: 'https://www.bodegaaurrera.com.mx/search?q=Cajeta+Hacienda+Real',
  },
  {
    name: 'Mercado Libre',
    logo: '/images/shops/mercado-libre-logo.png',
    tag: { en: 'Mexico & USA', es: 'México y EE. UU.' },
    url: 'https://www.mercadolibre.com.mx/p/MLM66925658',
  },
];

export function WhereToBuy() {
  const { t, lang } = useLanguage();
  const { ref, isInView } = useInView({ threshold: 0.15 });

  return (
    <Section id="where-to-buy">
      <Container ref={ref} $inView={isInView}>
        <Head>
          <div>
            <Eyebrow>{t('Find Us Online', 'Encuéntranos en Línea')}</Eyebrow>
            <Title>{t('Where to\nbuy us.', 'Dónde\ncomprarnos.')}</Title>
          </div>
          <Subtitle>{t('Available in Mexico and the United States.', 'Disponible en México y Estados Unidos.')}</Subtitle>
        </Head>

        <LogoGrid>
          {stores.map((store, i) => (
            <StoreCard
              key={i}
              href={store.url}
              target="_blank"
              rel="noopener noreferrer"
              $delay={i * 60}
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.6s ease ${i * 80}ms, transform 0.6s ease ${i * 80}ms, background 0.25s`,
              }}
            >
              <ArrowBtn><ArrowUpRight size={14} strokeWidth={1.8} /></ArrowBtn>
              <LogoBox>
                <img
                  src={store.logo}
                  alt={store.name}
                  style={store.logoScale ? { transform: `scale(${store.logoScale})` } : undefined}
                />
              </LogoBox>
            </StoreCard>
          ))}
        </LogoGrid>
      </Container>
    </Section>
  );
}
